import { useState } from 'react'
import { useNavigate, type LinkProps } from 'react-router-dom'
import { LuChevronDown } from 'react-icons/lu'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { encodeDavPath } from '../lib/webdavClient'

type Props = {
  basePath: string // e.g. "/networks/abc/files"
  currentPath: string // current folder path, e.g. "" or "foo/bar"
  state?: LinkProps['state']
}

// Renders the current folder name with a chevron-down that opens a dropdown of
// the hierarchy (root + each ancestor). Selecting an entry navigates up.
export default function FilePathSelect({ basePath, currentPath, state }: Props) {
  const navigate = useNavigate()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const segments = currentPath.split('/').filter(Boolean)
  const atRoot = segments.length === 0
  const label = atRoot ? 'Files' : segments[segments.length - 1]

  const crumbs: Array<{ label: string; path: string }> = [
    ...segments.map((seg, i) => ({
      label: seg,
      path: segments.slice(0, i + 1).join('/'),
    })).reverse(),
    { label: 'Files', path: '' },
  ]

  const go = (path: string) => {
    setAnchor(null)
    const encoded = encodeDavPath(path.split('/').filter(Boolean).join('/'))
    navigate(encoded ? `${basePath}/${encoded}` : basePath, { state })
  }

  return (
    <>
      <Button
        color="inherit"
        endIcon={atRoot ? undefined : <LuChevronDown />}
        onClick={atRoot ? undefined : e => setAnchor(e.currentTarget)}
        sx={{ pointerEvents: atRoot ? 'none' : undefined, backgroundColor: 'background.default', fontWeight: 500 }}
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {crumbs.map(crumb => (
          <MenuItem key={crumb.path || 'root'} selected={crumb.path === currentPath} onClick={() => go(crumb.path)}>
            {crumb.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
